import { useEffect, useMemo, useState } from 'react';
import { useGetConsistencyCollectionsQuery } from 'types/dt-graphql-apollo-v2';
import {
  ConsistencyReportSummary,
  ConsistencyRuleRunResult,
  ReportSortOption,
  REPORT_SORT_OPTIONS,
  aggregateRuns,
  deriveWorstStatus,
  isRuleDisabled,
  latestRunStatus,
  toRuleOperator,
  toRuleSeverity,
  mapConditionChecks,
} from '../consistencyReportTypes';

const POLL_INTERVAL_MS = 5000;

export { REPORT_SORT_OPTIONS };

type CollectionItems = NonNullable<
  NonNullable<
    ReturnType<typeof useGetConsistencyCollectionsQuery>['data']
  >['listConsistencyCollection']
>['items'];

export const mapCollectionsToReports = (
  collections: CollectionItems
): ConsistencyReportSummary[] => {
  const reports: ConsistencyReportSummary[] = [];

  for (const collection of collections) {
    if (!collection?.rules?.items) continue;

    const rulesWithRun = collection.rules.items.filter(
      (r) =>
        !isRuleDisabled(r?.tags) &&
        latestRunStatus(r?.runStatuses?.items)?.reportId
    );
    if (rulesWithRun.length === 0) continue;

    const firstRun = latestRunStatus(rulesWithRun[0]?.runStatuses?.items);
    if (!firstRun?.reportId) continue;

    // Find the reportId from the most recently executed rule. Using the first
    // rule in the list would pick up a stale reportId when different rules were
    // last run in different batches, causing the details page to show only a
    // subset of rules. Fall back to the first rule's reportId when no rule has
    // a timestamp (e.g. older data that pre-dates the runAt field).
    let reportId = firstRun.reportId;
    let mostRecentAt = firstRun.runAt ?? '';
    for (const rule of rulesWithRun) {
      const run = latestRunStatus(rule?.runStatuses?.items);
      if (run?.reportId && run.runAt && run.runAt > mostRecentAt) {
        mostRecentAt = run.runAt;
        reportId = run.reportId;
      }
    }

    // Aggregate across the latest run of every rule in the collection so the
    // card reflects the current state of the collection regardless of which
    // batch last touched each rule. The details page (useConsistencyReport)
    // mirrors this — it loads the collection by matching reportId and then
    // also picks the latest run per rule.
    const { statuses, totalValid, totalFailed, latestRunAt } = aggregateRuns(
      rulesWithRun.map((r) => latestRunStatus(r?.runStatuses?.items))
    );

    const schemaSet = new Set<string>();
    for (const sf of collection.schemaFilters?.items ?? []) {
      if (sf?.schemaType) schemaSet.add(sf.schemaType);
    }
    const schemas = Array.from(schemaSet);

    reports.push({
      reportId,
      collectionId: collection.externalId,
      collectionName: collection.name,
      status: deriveWorstStatus(statuses),
      runAt: latestRunAt,
      schemas,
      rulesCount: rulesWithRun.length,
      totalValid,
      totalFailed,
    });
  }

  return reports;
};

const sortReports = (
  option: ReportSortOption,
  reports: ConsistencyReportSummary[]
): ConsistencyReportSummary[] => {
  const sorted = [...reports];
  switch (option) {
    case 'nameASC':
      return sorted.sort((a, b) =>
        a.collectionName.localeCompare(b.collectionName)
      );
    case 'nameDESC':
      return sorted.sort((a, b) =>
        b.collectionName.localeCompare(a.collectionName)
      );
    case 'runTimeASC':
      return sorted.sort(
        (a, b) => new Date(b.runAt).getTime() - new Date(a.runAt).getTime()
      );
    case 'runTimeDESC':
      return sorted.sort(
        (a, b) => new Date(a.runAt).getTime() - new Date(b.runAt).getTime()
      );
    default:
      return sorted;
  }
};

export const mapRuleToRunResult = (
  rule: NonNullable<
    NonNullable<CollectionItems[number]>['rules']
  >['items'][number]
): ConsistencyRuleRunResult | null => {
  if (!rule) return null;
  const run = latestRunStatus(rule.runStatuses?.items);
  if (!run) return null;
  return {
    runStatusId: run.externalId,
    status: deriveWorstStatus([run.status ?? 'IN_PROGRESS']),
    ruleId: rule.externalId,
    ruleName: rule.name,
    customerDefinedId: rule.customerDefinedId ?? null,
    operator: toRuleOperator(rule.operator),
    sourceSchemaFilterName: rule.sourceSchemaFilter?.name ?? '',
    sourceAttribute: rule.sourceAttribute,
    targetSchemaFilterName: rule.targetSchemaFilter?.name ?? '',
    targetAttribute: rule.targetAttribute,
    severity: toRuleSeverity(rule.severity),
    errorMessage: rule.errorMessage ?? null,
    sourceFilters: rule.sourceFilters ?? null,
    targetFilters: rule.targetFilters ?? null,
    conditionChecks: mapConditionChecks(rule.conditionChecks?.items),
    totalValid: Number(run.totalValid ?? 0),
    totalFailed: Number(run.totalFailed ?? 0),
  };
};

export const useConsistencyReports = (digitalTwinId: string) => {
  // `cache-and-network` refreshes stale data on mount so navigating back to
  // the list after kicking off a run shows fresh totals.
  const { data, loading, error, startPolling, stopPolling } =
    useGetConsistencyCollectionsQuery({
      variables: { projectId: digitalTwinId },
      skip: !digitalTwinId,
      fetchPolicy: 'cache-and-network',
    });

  const [sortOption, setSortOption] = useState<ReportSortOption>(
    REPORT_SORT_OPTIONS[0].value
  );

  const reports = useMemo(() => {
    const collections = data?.listConsistencyCollection?.items ?? [];
    return sortReports(sortOption, mapCollectionsToReports(collections));
  }, [data, sortOption]);

  // Poll while any report has an IN_PROGRESS run so the grid auto-refreshes as
  // running jobs complete.
  const hasInProgress = reports.some((r) => r.status === 'IN_PROGRESS');
  useEffect(() => {
    if (hasInProgress) startPolling(POLL_INTERVAL_MS);
    else stopPolling();
  }, [hasInProgress, startPolling, stopPolling]);

  return {
    reports,
    loading,
    error,
    sortOption,
    setSortOption,
    selectedSortLabel:
      REPORT_SORT_OPTIONS.find((o) => o.value === sortOption)?.name ?? '',
  };
};
